import { Button, Field, FormError, Input, LoadingState } from '@lawbee/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Link2Off, LockKeyhole, MailCheck } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useParam, useSearchParam } from '../../lib/hooks';
import { PORTAL_ME_KEY, rememberPortalSlug } from '../../shell/PortalLayout';
import { PublicFrame } from '../../shell/RootPages';
import { useFirmQuery } from './firm';
import { ConfidentialityNotice } from './shared';

// ---------------------------------------------------------------------------
// Request a sign-in link
// ---------------------------------------------------------------------------

export function PortalSignInPage() {
  const slug = useParam('slug');
  const firm = useFirmQuery(slug);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) rememberPortalSlug(slug);
  }, [slug]);

  const request = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/api/public/portal/${encodeURIComponent(slug)}/request-link`, { email: email.trim() }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Enter the email address the firm has on file for you.');
      return;
    }
    setError(null);
    request.mutate();
  };

  const firmName = firm.data?.name ?? null;

  return (
    <PublicFrame firmName={firmName}>
      <div className="mx-auto max-w-md space-y-6 pt-4">
        {firm.isLoading ? (
          <LoadingState />
        ) : request.isSuccess ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl bg-card p-8 text-center shadow-card">
            <div className="flex size-14 items-center justify-center rounded-full bg-success/12 text-success">
              <MailCheck className="size-7" aria-hidden />
            </div>
            <h1 className="text-[22px] font-semibold tracking-[-0.02em]">Check your email</h1>
            <p className="text-[15px] leading-relaxed text-muted-foreground">
              If that email is on file, we sent a sign-in link to <span className="font-medium text-foreground">{email.trim()}</span>. The link works once and
              expires soon.
            </p>
            <Button variant="ghost" onClick={() => request.reset()}>
              Use a different email
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl bg-card p-6 shadow-card sm:p-8">
            <div className="mb-6 space-y-2 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <LockKeyhole className="size-5" aria-hidden />
              </div>
              <h1 className="text-[24px] font-semibold tracking-[-0.025em]">Client portal</h1>
              <p className="text-[15px] text-muted-foreground">
                {firmName ? `Sign in to see your matters with ${firmName}.` : 'Sign in to see your matters.'} We will email you a secure link, no password needed.
              </p>
            </div>
            <form noValidate onSubmit={submit} className="space-y-4">
              <Field label="Email address" htmlFor="portal-email" error={error}>
                <Input
                  id="portal-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={error ? true : undefined}
                />
              </Field>
              <FormError message={request.error ? errorMessage(request.error) : null} />
              <Button type="submit" size="lg" className="w-full" loading={request.isPending}>
                Email me a sign-in link
              </Button>
            </form>
          </div>
        )}
        {firm.data?.confidentialityNotice ? <ConfidentialityNotice notice={firm.data.confidentialityNotice} /> : null}
      </div>
    </PublicFrame>
  );
}

// ---------------------------------------------------------------------------
// Verify a magic link
// ---------------------------------------------------------------------------

export function PortalVerifyPage() {
  const token = useSearchParam('token');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<string | null>(token ? null : 'This sign-in link is incomplete.');

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    (async () => {
      try {
        await api.post('/portal-api/verify', { token });
        await qc.invalidateQueries({ queryKey: PORTAL_ME_KEY });
        await navigate({ to: '/portal/app', replace: true });
      } catch (err) {
        setError(errorMessage(err, 'This sign-in link could not be used.'));
      }
    })();
  }, [token, qc, navigate]);

  return (
    <PublicFrame>
      {error ? (
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-10 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Link2Off className="size-6" aria-hidden />
          </div>
          <h1 className="text-[22px] font-semibold tracking-[-0.02em]">This sign-in link has expired</h1>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            For your privacy, portal links work only once and expire after a short time. Request a new link from your firm&apos;s portal page.
          </p>
          <p className="text-xs text-muted-foreground">{error}</p>
          <PortalRetryLink />
        </div>
      ) : (
        <LoadingState label="Signing you in" className="min-h-[50vh]" />
      )}
    </PublicFrame>
  );
}

function PortalRetryLink() {
  let slug: string | null = null;
  try {
    slug = localStorage.getItem('lb-portal-slug');
  } catch {
    slug = null;
  }
  if (!slug) return <p className="text-sm text-muted-foreground">Use the portal link your law firm sent you to request a new one.</p>;
  return (
    <Button asChild>
      <Link to="/portal/$slug" params={{ slug }}>
        Get a new link
      </Link>
    </Button>
  );
}
